import { useNavigate } from "react-router-dom";
import { Charge } from "@/pages/sy/models/Charge";

export const useChargeHandlers = ({
  currentOperatorId,
  charge,
  maxCharges,
  charges,
  refetchCharges,
  chargeNumbers,
  setCurrentChargeSequenceNumber,
  setCurrentChargeNumber,
  setIsNewChargeMode,
  setError,
  setNotificationMessage,
  setIsChangingCharge,
}: {
  currentOperatorId: number | null;
  charge: Charge | null;
  maxCharges: number;
  charges: Charge[];
  refetchCharges: () => Promise<Charge[]>;
  chargeNumbers: number[];
  setCurrentChargeSequenceNumber: (chargeSequenceNumber: number | null) => void;
  setCurrentChargeNumber: (chargeNumber: number | null) => void;
  setIsNewChargeMode: (isNewChargeMode: boolean) => void;
  setError: (error: string) => void;
  setNotificationMessage: (message: string) => void;
  setIsChangingCharge: (isChanging: boolean) => void;
}) => {
  const navigate = useNavigate();

  const handleChargeSelect = (chargeNum: number) => {
    if (charges) {
      let found = null;

      // chargeNum is the ChargeNumber from the UI, so always search by ChargeNumber
      found = charges.find(
        (c: Charge) =>
          c.ChargeGroupNumber === charge?.ChargeGroupNumber &&
          c.ChargeNumber === chargeNum,
      );

      if (found) {
        if (found.ChargeId === charge?.ChargeId) {
          setIsChangingCharge(false);
          return;
        }

        setIsChangingCharge(true);
        const searchParams = new URLSearchParams(location.search);
        if (currentOperatorId) {
          searchParams.set("operatorId", currentOperatorId.toString());
        }

        navigate(
          `/scrap-charges/operation/${found.ChargeId}/continue/${
            found.ChargeGroupNumber
          }?${searchParams.toString()}`,
        );
        return;
      }
    }

    // Update both states to maintain consistency
    setCurrentChargeSequenceNumber(chargeNum);
    setCurrentChargeNumber(chargeNum);
  };

  const handleAddCharge = () => {
    const newChargeNumber = chargeNumbers.length + 1;
    if (newChargeNumber > maxCharges) {
      setError(`Maximum ${maxCharges} charges allowed`);
      return;
    }

    setIsNewChargeMode(true);
  };

  const handleNewChargeSchedule = async () => {
    try {
      setIsNewChargeMode(false);

      // Refetch charges through TanStack Query cache and get fresh data
      const allCharges = await refetchCharges();

      const newChargeNumber = chargeNumbers.length + 1;

      const newCharge = allCharges.find(
        (c: Charge) =>
          c.ChargeGroupNumber === charge?.ChargeGroupNumber &&
          c.ChargeNumber === newChargeNumber,
      );

      if (newCharge) {
        setIsChangingCharge(true);
        const searchParams = new URLSearchParams(location.search);
        if (currentOperatorId) {
          searchParams.set("operatorId", currentOperatorId.toString());
        }

        navigate(
          `/scrap-charges/operation/${newCharge.ChargeId}/continue/${
            newCharge.ChargeGroupNumber
          }?${searchParams.toString()}`,
        );
      }

      setNotificationMessage("Charge created successfully");
    } catch (err) {
      setError(
        "Error creating charge: " +
          (err instanceof Error ? err.message : String(err)),
      );
    }
  };

  const handleNewChargeCancel = () => {
    setIsNewChargeMode(false);
  };

  const handleNewChargeStart = () => {
    setIsNewChargeMode(false);
  };

  return {
    handleChargeSelect,
    handleAddCharge,
    handleNewChargeSchedule,
    handleNewChargeCancel,
    handleNewChargeStart,
  };
};
